import React, { useState, useEffect } from 'react';
import { 
  X,
  Share2,
  Copy,
  Check,
  ExternalLink,
  Twitter,
  Facebook,
  Linkedin,
  Mail,
  MessageCircle,
  Send,
  Link2
} from 'lucide-react';

// Enhanced Share Modal Component
const EnhancedShareModal = ({ analysis, isOpen, onClose }) => {
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    if (analysis) {
      // Generate share URL - you can customize this based on your routing
      const url = `${window.location.origin}/analysis/${analysis.id}`;
      setShareUrl(url);
    }
  }, [analysis]);

  if (!isOpen || !analysis) return null;

  // Share content formatting
  const shareData = {
    title: `Website Analysis: ${analysis.url}`,
    text: `Check out this AI analysis of ${analysis.url} - Overall Score: ${analysis.score || analysis.overallScore}/100! 🚀`,
    url: shareUrl,
    hashtags: 'WebAnalysis,AI,WebDev,Performance'
  };

  // Copy to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Social sharing functions
  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareData.text)}&url=${encodeURIComponent(shareData.url)}&hashtags=${shareData.hashtags}`;
    window.open(url, '_blank', 'width=550,height=420');
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareData.url)}&quote=${encodeURIComponent(shareData.text)}`;
    window.open(url, '_blank', 'width=580,height=400');
  };

  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareData.url)}&title=${encodeURIComponent(shareData.title)}&summary=${encodeURIComponent(shareData.text)}`;
    window.open(url, '_blank', 'width=520,height=570');
  };

  const shareToReddit = () => {
    const url = `https://reddit.com/submit?url=${encodeURIComponent(shareData.url)}&title=${encodeURIComponent(shareData.title)}`;
    window.open(url, '_blank', 'width=600,height=500');
  };

  const shareToWhatsApp = () => {
    const text = `${shareData.text}\n\n${shareData.url}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, '_blank');
  };

  const shareToTelegram = () => {
    const text = `${shareData.text}\n\n${shareData.url}`;
    const url = `https://t.me/share/url?url=${encodeURIComponent(shareData.url)}&text=${encodeURIComponent(shareData.text)}`;
    window.open(url, '_blank');
  };

  const shareToEmail = () => {
    const subject = encodeURIComponent(shareData.title);
    const body = encodeURIComponent(`${shareData.text}\n\nView the full analysis here: ${shareData.url}\n\nPowered by CodeRated - AI Website Analysis`);
    const url = `mailto:?subject=${subject}&body=${body}`;
    window.location.href = url;
  };

  // Native share (mobile)
  const handleNativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: shareData.title,
          text: shareData.text,
          url: shareData.url
        });
      } catch (error) {
        console.error('Native share failed:', error);
      }
    }
  };

  // Share platforms configuration
  const sharePlatforms = [
    {
      name: 'Twitter',
      icon: Twitter,
      color: 'hover:bg-blue-500/20 hover:text-blue-400',
      action: shareToTwitter
    },
    {
      name: 'Facebook', 
      icon: Facebook,
      color: 'hover:bg-blue-600/20 hover:text-blue-500',
      action: shareToFacebook
    },
    {
      name: 'LinkedIn',
      icon: Linkedin,
      color: 'hover:bg-blue-700/20 hover:text-blue-600',
      action: shareToLinkedIn
    },
    {
      name: 'Reddit',
      icon: MessageCircle,
      color: 'hover:bg-orange-500/20 hover:text-orange-400',
      action: shareToReddit
    },
    {
      name: 'WhatsApp',
      icon: MessageCircle,
      color: 'hover:bg-green-500/20 hover:text-green-400',
      action: shareToWhatsApp
    },
    {
      name: 'Telegram',
      icon: Send,
      color: 'hover:bg-blue-400/20 hover:text-blue-300',
      action: shareToTelegram
    },
    {
      name: 'Email',
      icon: Mail,
      color: 'hover:bg-gray-500/20 hover:text-gray-300',
      action: shareToEmail
    }
  ];

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md shadow-2xl">
        
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-slate-700">
          <div className="flex items-center gap-3">
            <Share2 className="w-5 h-5 text-blue-400" />
            <h3 className="text-lg font-bold text-white">Share Analysis</h3>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          
          {/* Preview */}
          <div className="bg-slate-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center text-white font-bold text-lg">
                {analysis.score || analysis.overallScore}
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-semibold truncate">{analysis.url}</h4>
                <p className="text-slate-400 text-sm">
                  AI Analysis Score: {analysis.score || analysis.overallScore}/100
                </p>
                <p className="text-slate-500 text-xs mt-1">
                  {analysis.demo_mode ? 'Demo Analysis' : 'AI-Powered Analysis'}
                </p>
              </div>
            </div>
          </div>

          {/* Copy Link */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Share Link
            </label>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2">
                <input
                  type="text"
                  value={shareUrl}
                  readOnly
                  className="w-full bg-transparent text-slate-300 text-sm focus:outline-none"
                />
              </div>
              <button
                onClick={handleCopyLink}
                className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                  copied 
                    ? 'bg-green-500/20 text-green-400 border border-green-500/30' 
                    : 'bg-slate-700 hover:bg-slate-600 text-slate-300'
                }`}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4 inline mr-1" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 inline mr-1" />
                    Copy
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Social Platforms */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-slate-300">
              Share to Platform
            </label>
            
            <div className="grid grid-cols-1 gap-2">
              {sharePlatforms.map((platform, index) => {
                const IconComponent = platform.icon;
                return (
                  <button
                    key={index}
                    onClick={platform.action}
                    className={`flex items-center gap-3 w-full p-3 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg transition-all ${platform.color}`}
                  >
                    <IconComponent className="w-5 h-5" />
                    <span className="font-medium">Share on {platform.name}</span>
                    <ExternalLink className="w-4 h-4 ml-auto opacity-60" />
                  </button>
                );
              })}
            </div>

            {/* Native Share (if available) */}
            {navigator.share && (
              <button
                onClick={handleNativeShare}
                className="flex items-center gap-3 w-full p-3 bg-gradient-to-r from-blue-500/20 to-purple-500/20 border border-blue-500/30 rounded-lg transition-all hover:from-blue-500/30 hover:to-purple-500/30"
              >
                <Share2 className="w-5 h-5" />
                <span className="font-medium">More Sharing Options</span>
              </button>
            )}
          </div>

          {/* Analytics Note */}
          <div className="mt-6 p-3 bg-slate-800/30 rounded-lg">
            <p className="text-slate-400 text-xs text-center">
              <Link2 className="w-3 h-3 inline mr-1" />
              Shared links help grow our community and improve AI analysis for everyone
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Updated Compact Report Modal with Enhanced Share
const CompactReportModalWithShare = ({ 
  analysis, 
  isOpen, 
  onClose, 
  onLike, 
  currentUser,
  currentTier = 'free' 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(analysis?.likes || 0);
  const [viewCount, setViewCount] = useState(analysis?.views || 0);
  const [showShareModal, setShowShareModal] = useState(false);

  // Check if user has liked this analysis
  useEffect(() => {
    if (analysis?.likedBy && currentUser?.id) {
      setIsLiked(analysis.likedBy.includes(currentUser.id));
    }
    setLikeCount(analysis?.likes || 0);
    setViewCount(analysis?.views || 0);
  }, [analysis, currentUser]);

  if (!isOpen || !analysis) return null;

  // Handle like toggle with immediate UI feedback
  const handleLike = async (e) => {
    e.stopPropagation();
    
    if (!currentUser) {
      alert('Please sign in to like analyses');
      return;
    }

    // Optimistic UI update
    const wasLiked = isLiked;
    setIsLiked(!wasLiked);
    setLikeCount(prev => wasLiked ? prev - 1 : prev + 1);

    try {
      // Call parent like handler
      if (onLike) {
        await onLike(analysis.id);
      }
    } catch (error) {
      // Revert on error
      setIsLiked(wasLiked);
      setLikeCount(prev => wasLiked ? prev + 1 : prev - 1);
      console.error('Like failed:', error);
    }
  };

  // Open share modal
  const handleShare = () => {
    setShowShareModal(true);
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-emerald-400';
    if (score >= 80) return 'text-green-400';
    if (score >= 70) return 'text-yellow-400';
    if (score >= 60) return 'text-orange-400';
    return 'text-red-400';
  };

  const ScoreCircle = ({ score, size = 'lg' }) => {
    const radius = size === 'sm' ? 20 : 32;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (score / 100) * circumference;
    
    return (
      <div className={`relative ${size === 'sm' ? 'w-12 h-12' : 'w-20 h-20'}`}>
        <svg className="transform -rotate-90 w-full h-full">
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="transparent"
            className="text-slate-700"
          />
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            stroke="currentColor"
            strokeWidth="3"
            fill="transparent"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className={getScoreColor(score)}
            style={{ transition: 'stroke-dashoffset 1s ease-in-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className={`font-bold ${size === 'sm' ? 'text-sm' : 'text-xl'} text-white`}>
            {Math.round(score)}
          </span>
          {size === 'lg' && (
            <span className="text-xs text-slate-400">/ 100</span>
          )}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
          
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-slate-700">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">{analysis.score || analysis.overallScore}</span>
              </div>
              <h2 className="text-xl font-bold text-white">DETAILED ANALYSIS</h2>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white transition-colors p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="overflow-y-auto max-h-[calc(90vh-80px)]">
            {/* Main Content */}
            <div className="p-6">
              
              {/* Website Header */}
              <div className="flex items-start justify-between mb-6">
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-white mb-3">{analysis.url}</h3>
                  
                  {/* Metadata Grid */}
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div className="flex items-center gap-2 text-slate-300">
                      <div className="w-4 h-4 bg-blue-500/20 rounded flex items-center justify-center">
                        <span className="text-xs">🏢</span>
                      </div>
                      <span className="text-slate-400">Industry:</span>
                      <span className="font-medium">{analysis.industry || 'Technology'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-300">
                      <div className="w-4 h-4 bg-green-500/20 rounded flex items-center justify-center">
                        <span className="text-xs">⏰</span>
                      </div>
                      <span className="text-slate-400">Analyzed:</span>
                      <span className="font-medium">{analysis.lastAnalyzed || 'Recently'}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-300">
                      <div className="w-4 h-4 bg-purple-500/20 rounded flex items-center justify-center">
                        <span className="text-xs">👀</span>
                      </div>
                      <span className="text-slate-400">Views:</span>
                      <span className="font-medium">{viewCount}</span>
                    </div>
                    
                    <div className="flex items-center gap-2 text-slate-300">
                      <div className="w-4 h-4 bg-red-500/20 rounded flex items-center justify-center">
                        <span className="text-xs">❤️</span>
                      </div>
                      <span className="text-slate-400">Likes:</span>
                      <span className="font-medium">{likeCount}</span>
                    </div>
                  </div>
                  
                  {/* Source Badge */}
                  <div className="mt-3">
                    <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm ${
                      analysis.demo_mode 
                        ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' 
                        : 'bg-green-500/20 text-green-300 border border-green-500/30'
                    }`}>
                      {analysis.demo_mode ? '🎭' : '🤖'}
                      <span>Source: {analysis.demo_mode ? 'Demo Analysis' : 'AI Analysis'}</span>
                    </span>
                  </div>
                </div>
                
                {/* Score Display */}
                <div className="text-center ml-6">
                  <div className="mb-2">
                    <ScoreCircle score={analysis.score || analysis.overallScore} />
                  </div>
                  <div className="text-sm text-slate-400 font-medium">OVERALL SCORE</div>
                  <div className={`text-2xl font-bold ${getScoreColor(analysis.score || analysis.overallScore)}`}>
                    {analysis.score || analysis.overallScore}/100
                  </div>
                </div>
              </div>

              {/* Score Breakdown */}
              {analysis.scores && (
                <div className="mb-6">
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
                    {Object.entries(analysis.scores).map(([key, score]) => (
                      <div key={key} className="text-center bg-slate-800/50 rounded-lg p-3">
                        <ScoreCircle score={score} size="sm" />
                        <div className="text-xs text-slate-400 mt-1 capitalize">
                          {key.replace('_', ' ')}
                        </div>
                        <div className={`text-sm font-bold mt-1 ${getScoreColor(score)}`}>
                          {Math.round(score)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Key Insights */}
              <div className="space-y-4 mb-6">
                
                {/* What's Working Well */}
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 bg-green-500/20 rounded flex items-center justify-center">
                      <span className="text-xs">✅</span>
                    </div>
                    <h4 className="text-green-300 font-semibold">WHAT'S WORKING WELL:</h4>
                  </div>
                  <p className="text-green-200 text-sm leading-relaxed">
                    {analysis.insights?.working || 'Exceptional brand consistency and premium user experience design. Perfect visual hierarchy and seamless navigation flow.'}
                  </p>
                </div>

                {/* Improvement Opportunities */}
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 bg-yellow-500/20 rounded flex items-center justify-center">
                      <span className="text-xs">🚀</span>
                    </div>
                    <h4 className="text-yellow-300 font-semibold">IMPROVEMENT OPPORTUNITIES:</h4>
                  </div>
                  <p className="text-yellow-200 text-sm leading-relaxed">
                    {analysis.insights?.improvements || analysis.topIssue || 'International page load speeds could be optimized with better CDN coverage and image compression.'}
                  </p>
                </div>

                {/* Strategic Insights - Expandable */}
                {isExpanded && (
                  <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-5 h-5 bg-blue-500/20 rounded flex items-center justify-center">
                        <span className="text-xs">💡</span>
                      </div>
                      <h4 className="text-blue-300 font-semibold">STRATEGIC INSIGHTS:</h4>
                    </div>
                    <p className="text-blue-200 text-sm leading-relaxed">
                      {analysis.insights?.strategic || 'Market leadership position is strong - focus on emerging market expansion opportunities and premium segment growth.'}
                    </p>
                  </div>
                )}
              </div>

              {/* Expand/Collapse Button */}
              <div className="text-center mb-6">
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="flex items-center gap-2 mx-auto text-slate-400 hover:text-white transition-colors text-sm"
                >
                  {isExpanded ? (
                    <>
                      <span>Show Less</span>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <span className="text-xs">▲</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <span>Show More Insights</span>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <span className="text-xs">▼</span>
                      </div>
                    </>
                  )}
                </button>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-700">
                
                {/* Like Button */}
                <button
                  onClick={handleLike}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    isLiked 
                      ? 'bg-red-500/20 text-red-300 border border-red-500/30' 
                      : 'bg-slate-700 hover:bg-slate-600 text-slate-300 hover:text-red-300'
                  }`}
                  disabled={!currentUser}
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <span className={`text-sm ${isLiked ? '❤️' : '🤍'}`}>
                      {isLiked ? '❤️' : '🤍'}
                    </span>
                  </div>
                  <span className="text-sm">{likeCount}</span>
                </button>

                {/* Center Actions */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleShare}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-all"
                  >
                    <Share2 className="w-4 h-4" />
                    <span className="text-sm">Share</span>
                  </button>
                  
                  <button
                    onClick={() => window.open(`https://${analysis.url}`, '_blank')}
                    className="flex items-center gap-2 bg-slate-700 hover:bg-slate-600 text-slate-300 px-4 py-2 rounded-lg transition-all"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span className="text-sm">Visit</span>
                  </button>
                </div>

                {/* Re-analyze Button */}
                <button
                  onClick={() => {
                    onClose();
                    window.location.href = `/search?demo=${encodeURIComponent(analysis.url)}`;
                  }}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-all"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <span className="text-sm">🔄</span>
                  </div>
                  <span className="text-sm">Re-analyze</span>
                </button>
              </div>

              {/* Premium Upgrade CTA */}
              {currentTier === 'free' && (
                <div className="mt-6 bg-gradient-to-r from-blue-900/40 to-purple-900/40 border border-blue-700/50 rounded-lg p-4 text-center">
                  <div className="flex items-center justify-center gap-2 mb-2">
                    <div className="w-5 h-5 flex items-center justify-center">
                      <span className="text-yellow-400">⭐</span>
                    </div>
                    <h4 className="text-white font-semibold">Unlock Full Insights</h4>
                  </div>
                  <p className="text-slate-300 text-sm mb-3">
                    Get unlimited analyses, detailed reports, and premium features
                  </p>
                  <button
                    onClick={() => window.location.href = '/pricing'}
                    className="bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-lg text-sm font-semibold hover:from-blue-600 hover:to-purple-700 transition-all"
                  >
                    <div className="flex items-center gap-2">
                      <span className="text-sm">🏆</span>
                      <span>Upgrade Now</span>
                    </div>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Share Modal */}
      <EnhancedShareModal
        analysis={analysis}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
};

export default CompactReportModalWithShare;